/* eslint-disable max-lines */
import type { PropsWithChildren } from "react";
import { atom, useAtomValue } from "jotai";
import { useHydrateAtoms } from "jotai/utils";

import {
  isFinalVerificationMobileVariant1,
  isFinalVerificationMobileVariant2,
  shouldShowAiStudio as shouldShowAiStudioUtil,
  shouldShowDesktopFinalVerification,
  shouldShowNegativeSpaceTool
} from "./experiments";
import { DEFAULT_FREE_SHIPPING_THRESHOLD } from "../constants/constants";

export type DesignLabConfig = {
  flip?: {
    paidExpertEditAddon?: boolean;
    madeSamplesV1?: boolean;
    designLabCommunityArt?: boolean;
    cachedDeliveryDateAvailable?: boolean;
    manualBgRemovalBrushSize?: boolean;
    weaveAiImageRefinement?: boolean;
    editToolsAiImageRefinement?: boolean;
    dtfWorkerEnhancement?: boolean;
    dtfBackendTriggeredEnhancement?: boolean;
    designLabNewEnhancementTrigger?: boolean;
    customerReviewOnPrintRedraws?: boolean;
    customerReviewOnRedraws?: boolean;
  };
  experiments?: {
    abSmartRedrawNotes?: number;
    abShippingEnhancementServices?: number;
    abWeaverAiStudio?: number;
    abDesignLabFinalVerification?: number;
    abDesignLabNegativeSpaceTool?: number;
    abDesignLabReorderTabs?: number;
  };
  is_sample?: boolean;
  isSample?: boolean;
  ai_design?: boolean;
  aiDesign?: boolean;
  aiCreditsBalance?: number;
  cachedEstimatedDeliveryDate?: string;
  orderNumber?: string;
  fbData?: string;
  supportPhoneNumber: string;
  showLocalDeliveryToggle?: boolean;
  localDeliveryAddressEligible?: boolean;
  defaultCompositionId: string;
  isStaff?: boolean;
  freeShippingThreshold?: number;
  isFreeShippingThresholdZero?: boolean;
  printReseller?: boolean;
};

export type ConfigProviderProps = {
  config: DesignLabConfig;
};

type DesignLabComputedConfig = ReturnType<typeof computeDesignLabConfig>;

const designLabConfigAtom = atom<DesignLabConfig | null>(null);

function computeDesignLabConfig(config: DesignLabConfig) {
  const shouldShowArtistEditAddon = config.flip?.paidExpertEditAddon;
  const shouldShowSmartRedrawNotes = config.experiments?.abSmartRedrawNotes === 1;
  const shouldShowExpertTab = !config.flip?.paidExpertEditAddon;
  const shouldShowArtistInstructions = shouldShowSmartRedrawNotes;
  const shouldShowAiStudio = shouldShowAiStudioUtil(config.experiments?.abWeaverAiStudio);

  const shouldExperiments = config.experiments;

  // Support both snake_case and camelCase versions
  const isSample = config.isSample || config.is_sample || false;
  const aiDesign = config.aiDesign || config.ai_design || false;
  const isStaff = config.isStaff || false;

  const shouldShowCommunityArt = config.flip?.designLabCommunityArt || false;
  const shouldShowCachedDeliveryDate = config.flip?.cachedDeliveryDateAvailable || false;
  const { cachedEstimatedDeliveryDate } = config;
  const shouldShowManualBgRemovalBrushSize = config.flip?.manualBgRemovalBrushSize || false;
  const shouldShowNegativeSpaceAutomation = shouldShowNegativeSpaceTool(
    config.experiments?.abDesignLabNegativeSpaceTool
  );

  const shouldShowDesktopFinalVerificationFeature = shouldShowDesktopFinalVerification(
    config.experiments?.abDesignLabFinalVerification
  );
  const shouldShowFinalVerificationMobileVariant1 = isFinalVerificationMobileVariant1(
    config.experiments?.abDesignLabFinalVerification
  );
  const shouldShowFinalVerificationMobileVariant2 = isFinalVerificationMobileVariant2(
    config.experiments?.abDesignLabFinalVerification
  );

  const flip = (config.flip ?? {}) as Record<string, boolean>;
  const shouldShowWeaveAiImageRefinement = flip["weaveAiImageRefinement"] ?? false;
  const shouldShowEditToolsAiImageRefinement = flip["editToolsAiImageRefinement"] ?? false;
  const { supportPhoneNumber, defaultCompositionId } = config;
  const dtfWorkerEnhancement = flip["dtfWorkerEnhancement"] ?? false;
  const dtfBackendTriggeredEnhancement = flip["dtfBackendTriggeredEnhancement"] ?? false;
  const showLocalDeliveryToggle = config.showLocalDeliveryToggle ?? false;
  const localDeliveryAddressEligible = config.localDeliveryAddressEligible ?? false;
  const shouldUseDesignLabNewEnhancementTrigger = flip["designLabNewEnhancementTrigger"] ?? false;
  const shouldShowCustomerReviewOnPrintRedraws = flip["customerReviewOnPrintRedraws"] ?? false;
  const shouldShowCustomerReviewOnRedraws = flip["customerReviewOnRedraws"] ?? false;
  const freeShippingThreshold = config.freeShippingThreshold ?? DEFAULT_FREE_SHIPPING_THRESHOLD;
  const isFreeShippingThresholdZero = config.isFreeShippingThresholdZero ?? false;
  const printReseller = config.printReseller ?? false;

  return {
    config,
    shouldShowArtistEditAddon,
    shouldShowExpertTab,
    shouldShowSmartRedrawNotes,
    shouldShowArtistInstructions,
    shouldShowAiStudio,
    shouldExperiments,
    isSample,
    aiDesign,
    isStaff,
    shouldShowCommunityArt,
    shouldShowCachedDeliveryDate,
    cachedEstimatedDeliveryDate,
    shouldShowManualBgRemovalBrushSize,
    shouldShowNegativeSpaceAutomation,
    shouldShowDesktopFinalVerificationFeature,
    shouldShowFinalVerificationMobileVariant1,
    shouldShowFinalVerificationMobileVariant2,
    shouldShowWeaveAiImageRefinement,
    shouldShowEditToolsAiImageRefinement,
    supportPhoneNumber,
    defaultCompositionId,
    dtfWorkerEnhancement,
    dtfBackendTriggeredEnhancement,
    showLocalDeliveryToggle,
    localDeliveryAddressEligible,
    shouldUseDesignLabNewEnhancementTrigger,
    shouldShowCustomerReviewOnPrintRedraws,
    shouldShowCustomerReviewOnRedraws,
    freeShippingThreshold,
    isFreeShippingThresholdZero,
    printReseller
  };
}

const designLabComputedConfigAtom = atom<DesignLabComputedConfig | null>(get => {
  const config = get(designLabConfigAtom);
  if (!config) return null;
  return computeDesignLabConfig(config);
});

function useDesignLabComputedConfig() {
  const computed = useAtomValue(designLabComputedConfigAtom);
  if (!computed) {
    throw new Error("ConfigProvider is missing");
  }
  return computed;
}

export function ConfigProvider({ config, children }: PropsWithChildren<ConfigProviderProps>) {
  useHydrateAtoms([[designLabConfigAtom, config]]);
  return <>{children}</>;
}

export function useDesignLabConfig() {
  return useDesignLabComputedConfig().config;
}

export function useDtfBackendTriggeredEnhancement() {
  return useDesignLabComputedConfig().dtfBackendTriggeredEnhancement;
}

export function useDtfWorkerEnhancement() {
  return useDesignLabComputedConfig().dtfWorkerEnhancement;
}

export function useShowArtistEditAddon() {
  return useDesignLabComputedConfig().shouldShowArtistEditAddon;
}

export function useShowExpertTab() {
  return useDesignLabComputedConfig().shouldShowExpertTab;
}

export function useShowSmartRedrawNotes() {
  return useDesignLabComputedConfig().shouldShowSmartRedrawNotes;
}

export function useShowArtistInstructions() {
  return useDesignLabComputedConfig().shouldShowArtistInstructions;
}

export function useShowAiStudio() {
  return useDesignLabComputedConfig().shouldShowAiStudio;
}

export function useExperiments() {
  return useDesignLabComputedConfig().shouldExperiments;
}

export function useIsSample() {
  return useDesignLabComputedConfig().isSample;
}

export function useAiDesign() {
  return useDesignLabComputedConfig().aiDesign;
}

export function useIsStaff() {
  return useDesignLabComputedConfig().isStaff;
}

export function useShowCommunityArt() {
  return useDesignLabComputedConfig().shouldShowCommunityArt;
}

export function useShowCachedDeliveryDate() {
  return useDesignLabComputedConfig().shouldShowCachedDeliveryDate;
}

export function useCachedEstimatedDeliveryDate() {
  return useDesignLabComputedConfig().cachedEstimatedDeliveryDate;
}

export function useShowManualBgRemovalBrushSize() {
  return useDesignLabComputedConfig().shouldShowManualBgRemovalBrushSize;
}

export function useShowNegativeSpaceAutomation() {
  return useDesignLabComputedConfig().shouldShowNegativeSpaceAutomation;
}

export function useShowDesktopFinalVerification() {
  return useDesignLabComputedConfig().shouldShowDesktopFinalVerificationFeature;
}

export function useShowFinalVerificationMobileVariant1() {
  return useDesignLabComputedConfig().shouldShowFinalVerificationMobileVariant1;
}

export function useShowFinalVerificationMobileVariant2() {
  return useDesignLabComputedConfig().shouldShowFinalVerificationMobileVariant2;
}

export function useShowWeaveAiImageRefinement() {
  return useDesignLabComputedConfig().shouldShowWeaveAiImageRefinement;
}

export function useShowEditToolsAiImageRefinement() {
  return useDesignLabComputedConfig().shouldShowEditToolsAiImageRefinement;
}

export function useSupportPhoneNumber() {
  return useDesignLabComputedConfig().supportPhoneNumber;
}

export function useShowLocalDeliveryToggle() {
  return useDesignLabComputedConfig().showLocalDeliveryToggle;
}

export function useLocalDeliveryAddressEligible() {
  return useDesignLabComputedConfig().localDeliveryAddressEligible;
}

export function useDefaultCompositionId() {
  return useDesignLabComputedConfig().defaultCompositionId;
}

export function useDesignLabNewEnhancementTrigger() {
  return useDesignLabComputedConfig().shouldUseDesignLabNewEnhancementTrigger;
}

export function useFreeShippingThreshold() {
  return useDesignLabComputedConfig().freeShippingThreshold;
}

export function useIsFreeShippingThresholdZero() {
  return useDesignLabComputedConfig().isFreeShippingThresholdZero;
}

export function usePrintReseller() {
  return useDesignLabComputedConfig().printReseller;
}
